import { DescriptionAndTags, IModelSelector, InfoLabel, NameInput, ThumbnailUpload } from "components/actions/forms";
import { LoadingSpinner } from "components/loading-spinner/LoadingSpinner";
import { useEffect, useState } from "react";
import { FieldValues, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { routeTo } from "router";
import { runtimeEnv } from "configuration";
import { sandboxApi } from "services/sandbox-api/api";
import { SandboxApi } from "services/sandbox-api/types";
import { IModelInfo } from "services/SandboxIModels";
import { useAppDispatch } from "store";
import { sandboxActionInvoked, sandboxImodelsChanged, sandboxOptionalFilesChanged } from "store/features";
import { OptionalFileSet } from "types";
import { Alert, Button, Dialog } from "@itwin/itwinui-react";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { DialogTabs } from "./dialog-tabs";
import { OptionalFilesTable } from "./optional-files/OptionalFilesTable";
import styles from "./SettingsDialog.module.css";

interface SettingsDialogProps {
  sandboxId?: string;
  userId?: string;
  onClose?: () => void;
}

interface FormData extends FieldValues {
  name?: string;
  description?: string;
  imodels?: IModelInfo[];
  optionalFiles?: OptionalFileSet[];
  thumbnail?: string | null;
};

export const SettingsDialog = ({ sandboxId: codeshareId, userId, onClose }: SettingsDialogProps) => {
  const navigate = useNavigate();
  const { data, isLoading } = sandboxApi.useCodeShareQuery(userId && codeshareId ? { userId, codeshareId } : skipToken);
  const { data: thumbnail, isLoading: isThumbnailLoading } = sandboxApi.useThumbnailQuery(data && userId && codeshareId ? { userId, codeshareId, isAvailable: data.isThumbnailAdded } : skipToken);

  const [update, { isLoading: isUpdating }] = sandboxApi.useCodeShareUpdateMutation();
  const [thumbnailUpload, { isLoading: isThumbnailUploading }] = sandboxApi.useThumbnailUploadMutation();
  const [thumbnailDelete, { isLoading: isThumbnailDeleting }] = sandboxApi.useThumbnailDeleteMutation();

  const {
    handleSubmit,
    control,
    reset,
    formState: { dirtyFields }
  } = useForm<FormData>();

  const dispatch = useAppDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const onCancel = () => {
    setIsModalOpen(false);
    onClose && setTimeout(onClose, 200);
  };

  const onUpdate = () => {
    setIsModalOpen(false);
    onClose && setTimeout(() => {
      onClose();
      dispatch(sandboxActionInvoked({ type: "update", args: { userId: userId!, sandboxId: codeshareId! } }));
    }, 200);
  }

  useEffect(() => {
    setTimeout(() => setIsModalOpen(true), 5);
  }, []);

  useEffect(() => {
    if (data) {
      reset({
        name: data?.name,
        description: data?.description,
        imodels: data?.allowedImodels ?? [],
        optionalFiles: data?.optionalFiles,
      });
    }

    if (thumbnail) {
      reset({
        name: data?.name,
        description: data?.description,
        imodels: data?.allowedImodels ?? [],
        optionalFiles: data?.optionalFiles,
        thumbnail
      });
    }
  }, [data, thumbnail]);

  const onSubmit = (fields: FormData) => {
    if (!data)
      return;

    const payload: SandboxApi.CodeShareUpdate = {
      id: data.id,
      userId: data.userId,
    }

    if (dirtyFields.name) { payload.name = fields.name?.trim(); }
    if (dirtyFields.description) payload.description = fields.description?.trim();
    if (dirtyFields.imodels /*!areListsEqual(["name"], data.allowedImodels, fields.imodels)*/) {
      payload.iModels = (fields.imodels ?? []).map(item => item.name);
      dispatch(sandboxImodelsChanged(fields.imodels ?? []));
    }
    if (dirtyFields.optionalFiles /* !areListsEqual(["name", "version"], data.optionalFiles, fields.optionalFiles)*/) {
      payload.optionalFiles = fields.optionalFiles;
      dispatch(sandboxOptionalFilesChanged(fields.optionalFiles ?? []));
    }

    if (dirtyFields.thumbnail && fields.thumbnail) {
      fetch(fields.thumbnail).then((response) => response.blob()).then((blob) => {
        thumbnailUpload({ userId: data.userId, codeshareId: data.id, thumbnail: new File([blob], "thumbnail") });
      });
    }
    if (dirtyFields.thumbnail && fields.thumbnail === null) {
      thumbnailDelete({ codeshareId: data.id });
    }

    if (Object.keys(payload).length > 2) {
      update(payload).unwrap().then(() => {
        if (data.name !== fields.name)
          navigate(routeTo.sandbox({ userName: data.username, sandboxName: fields.name! }));
      });
    }
  }

  return (
    <Dialog isOpen={isModalOpen} onClose={onCancel} closeOnEsc closeOnExternalClick preventDocumentScroll trapFocus setFocus isDismissible>
      <Dialog.Backdrop />
      <Dialog.Main className={styles.dialog} data-testid="atp-sandbox-settings-dialog">
        <Dialog.TitleBar titleText="Sandbox Settings" />

        {/* onSubmit gets fired every time switching tabs after optional files has changed.
          react-hook-form docs states it doesn't work with tabs (each tab must be a separate form)
          DialogTabs component modifies visibility of tab body to prevent rerenders, but it doesn't help with onSubmit problem.
          A workaround is to give an empty function to form's onSubmit handler and do data save at onClick directly attached to the primary button
          The workaround makes the form function as expected, but I don't have an explanation. More research is needed.
      */}
        <form onSubmit={handleSubmit(() => { })} noValidate >
          <Dialog.Content>
            {!data || isLoading || isThumbnailLoading
              ? <LoadingSpinner centered message="Loading Settings..." />
              : <div>
                {data.itwinVersion && runtimeEnv.REACT_APP_PREFERRED_VERSION !== data.itwinVersion &&
                  <Alert
                    type="warning"
                    className={styles.versionWarning}
                    clickableText="Update"
                    clickableTextProps={{ onClick: onUpdate }}>
                    {`This sandbox uses an older version of iTwin.js (${data.itwinVersion})`}
                  </Alert>
                }
                <DialogTabs>
                  <div className={styles.generalSettings}>
                    <div className={styles.columnLeft}>
                      <NameInput name="name" control={control} userName={data.username} />
                      <DescriptionAndTags name="description" control={control} />
                      <IModelSelector name="imodels" control={control} />
                    </div>
                    <div className={styles.columnRight}>
                      <div>
                        <InfoLabel label="Thumbnail" toolTip="Create and attach a thumbnail for a preview of the sandbox" />
                        <ThumbnailUpload name="thumbnail" control={control} />
                      </div>
                    </div>
                  </div>
                  <OptionalFilesTable name="optionalFiles" control={control} />
                </DialogTabs>
              </div>}
          </Dialog.Content>
          <Dialog.ButtonBar>
            {(isUpdating || isThumbnailDeleting || isThumbnailUploading) && <LoadingSpinner centered hPos="left" size="small" message="Saving..." />}
            <Button
              styleType="high-visibility"
              onClick={handleSubmit((fields) => { onSubmit(fields) })}
              disabled={isUpdating || isThumbnailDeleting || isThumbnailUploading || Object.keys(dirtyFields).length === 0}
              data-testid="atp-save-btn">
              Save
            </Button>
            <Button onClick={onCancel} data-testid="atp-close-btn">Close</Button>
          </Dialog.ButtonBar>
        </form>
      </Dialog.Main>
    </Dialog>
  )
};
